import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportSalesByContact from "hooks/organization/reports/types/useGetReportSalesByContact";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableSalesByContact(startDate, endDate, companyId = null) {
  const [getReportSalesByContact, { data, loading, error }] = useGetReportSalesByContact(false); // No success message for filtering/pagination

  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        startDate,
        endDate,
        companyId,
        page,
        first,
      };
      getReportSalesByContact({ variables });
    },
    [startDate, endDate, companyId, getReportSalesByContact]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportSalesByContact?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Customer Name",
        accessor: "customerName",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Product",
        accessor: "product",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Job Size",
        accessor: "jobSize",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
      },
      {
        Header: "Total",
        accessor: "total",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportSalesByContact?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportSalesByContact: paginate,
  } as const;
}
